import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPut, apiDelete } from "../../utils/apiClient";
import { Link } from "react-router-dom";
import { useUserContext } from "../../contexts/UserContext";

type Notification = {
    id: string;
    tenantId?: string;
    isGlobal?: boolean;

    title: string;
    level: "info" | "warning" | "critical";
    targetType: "all" | "company" | "shop";
    isActive: boolean;
    createdAt?: any;

    canEdit?: boolean;
    canDelete?: boolean;
};

const OEM_TENANT_ID = "ecvision";

function pickTenantIdFromContext(ctx: any): string {
    const candidates = [
        ctx?.tenantId,
        ctx?.user?.tenantId,
        ctx?.me?.tenantId,
        ctx?.profile?.tenantId,
        ctx?.decoded?.tenantId,
    ];
    for (const c of candidates) {
        const t = String(c ?? "").trim();
        if (t) return t;
    }
    return "";
}

function levelBadge(level: Notification["level"]) {
    if (level === "critical") return "bg-red-50 text-red-700 border-red-200";
    if (level === "warning") return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-sky-50 text-sky-700 border-sky-200";
}

function activeBadge(on: boolean) {
    return on
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-gray-50 text-gray-600 border-gray-200";
}

function targetBadge(t: Notification["targetType"]) {
    if (t === "shop") return "bg-violet-50 text-violet-700 border-violet-200";
    if (t === "company") return "bg-indigo-50 text-indigo-700 border-indigo-200";
    return "bg-gray-50 text-gray-600 border-gray-200";
}

export default function SuperNotificationsPage() {
    const ctx = useUserContext();

    const myTenantId = useMemo(() => pickTenantIdFromContext(ctx), [ctx]);
    const isEcvision = myTenantId === OEM_TENANT_ID;
    const myTenantLabel = myTenantId || "-";

    const [rows, setRows] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    // compact filters
    const [q, setQ] = useState("");
    const [onlyActive, setOnlyActive] = useState(false);
    const [onlyMine, setOnlyMine] = useState(false);
    const [onlyGlobal, setOnlyGlobal] = useState(false);
    const [level, setLevel] = useState<"" | Notification["level"]>("");
    const [targetType, setTargetType] = useState<"" | Notification["targetType"]>("");

    const load = useCallback(async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const res = await apiGet("/api/super/notifications");
            const json = await res.json().catch(() => null);
            setRows(Array.isArray(json) ? json : []);
        } catch (e: any) {
            setRows([]);
            setErrorMsg(e?.message ?? "読み込みに失敗しました");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const canEditRow = useCallback(
        (r: Notification) => {
            if (typeof r.canEdit === "boolean") return r.canEdit;
            const t = String(r.tenantId ?? "").trim();
            return isEcvision || (!!myTenantId && t === myTenantId);
        },
        [isEcvision, myTenantId]
    );

    const canDeleteRow = useCallback(
        (r: Notification) => {
            if (typeof r.canDelete === "boolean") return r.canDelete;
            const t = String(r.tenantId ?? "").trim();
            return isEcvision || (!!myTenantId && t === myTenantId);
        },
        [isEcvision, myTenantId]
    );

    const toggleActive = useCallback(
        async (row: Notification) => {
            if (!canEditRow(row)) return;
            if (busyId) return;

            setBusyId(row.id);
            try {
                await apiPut(`/api/super/notifications/${row.id}`, { isActive: !row.isActive });
                await load();
            } finally {
                setBusyId(null);
            }
        },
        [canEditRow, busyId, load]
    );

    const askDelete = (id: string) => setPendingDeleteId(id);
    const cancelDelete = () => setPendingDeleteId(null);

    const doDelete = useCallback(
        async (row: Notification) => {
            if (!canDeleteRow(row)) return;
            if (busyId) return;

            setBusyId(row.id);
            try {
                await apiDelete(`/api/super/notifications/${row.id}`);
                setPendingDeleteId(null);
                await load();
            } finally {
                setBusyId(null);
            }
        },
        [canDeleteRow, busyId, load]
    );

    const visibleRows = useMemo(() => {
        const k = q.trim().toLowerCase();

        return rows
            .filter((r) => {
                if (onlyActive && !r.isActive) return false;
                if (onlyGlobal && r.isGlobal !== true) return false;

                if (onlyMine) {
                    const t = String(r.tenantId ?? "").trim();
                    if (!myTenantId) return false;
                    if (t !== myTenantId) return false;
                }

                if (level && r.level !== level) return false;
                if (targetType && r.targetType !== targetType) return false;

                if (!k) return true;

                const tenantLabel = String(r.tenantId ?? "").trim() || "-";
                const hay = `${r.title} ${r.level} ${r.targetType} ${tenantLabel} ${r.isGlobal ? "global" : ""}`.toLowerCase();
                return hay.includes(k);
            })
            .sort((a, b) => {
                // Active を上に、次に level、次に title
                if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                const lv = (x: Notification["level"]) => (x === "critical" ? 0 : x === "warning" ? 1 : 2);
                const d = lv(a.level) - lv(b.level);
                if (d !== 0) return d;
                return String(a.title ?? "").localeCompare(String(b.title ?? ""), "ja");
            });
    }, [rows, q, onlyActive, onlyMine, onlyGlobal, level, targetType, myTenantId]);

    // compact classes
    const wrap = "h-full min-h-0 flex flex-col gap-2 overflow-hidden p-2";
    const card = "bg-white border rounded-md px-2 py-2";
    const btn = "text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50";
    const th = "border px-2 py-1 whitespace-nowrap sticky top-0 z-10 bg-gray-100 text-[11px] font-semibold";
    const td = "border px-2 py-1 text-xs";

    if (loading) return <div className="p-2 text-xs text-gray-600">Loading...</div>;

    return (
        <div className={wrap}>
            {/* header */}
            <div className={card}>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <h1 className="text-sm font-semibold">お知らせ管理</h1>

                        <span className="text-[11px] px-2 py-0.5 rounded border bg-gray-50 text-gray-700 shrink-0">
                            tenant: {myTenantLabel}
                        </span>

                        {isEcvision && (
                            <span className="text-[11px] px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 shrink-0">
                                OEM
                            </span>
                        )}

                        <span className="text-[11px] text-gray-500 ml-1">
                            表示: {visibleRows.length} / 全 {rows.length}
                        </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={load} className={btn} disabled={!!busyId}>
                            再読み込み
                        </button>

                        <Link to="/super/notifications/new" className="text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50">
                            ＋ 新規作成
                        </Link>
                    </div>
                </div>

                {errorMsg && (
                    <div className="mt-2 text-xs text-red-700 border border-red-200 bg-red-50 rounded px-2 py-1">
                        {errorMsg}
                    </div>
                )}

                {/* filters */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                        className="border rounded px-2 py-1 text-xs w-64"
                        placeholder="検索: title / tenant / level / target"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />

                    <label className="text-xs flex items-center gap-1 select-none">
                        <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />
                        ONのみ
                    </label>

                    <label className="text-xs flex items-center gap-1 select-none">
                        <input type="checkbox" checked={onlyGlobal} onChange={(e) => setOnlyGlobal(e.target.checked)} />
                        全体のみ
                    </label>

                    <label className="text-xs flex items-center gap-1 select-none">
                        <input
                            type="checkbox"
                            checked={onlyMine}
                            onChange={(e) => setOnlyMine(e.target.checked)}
                            disabled={!myTenantId}
                            title={!myTenantId ? "tenantId が取得できないため無効" : undefined}
                        />
                        自分のテナントのみ
                    </label>

                    <select className="border rounded px-2 py-1 text-xs" value={level} onChange={(e) => setLevel(e.target.value as any)}>
                        <option value="">level: all</option>
                        <option value="info">info</option>
                        <option value="warning">warning</option>
                        <option value="critical">critical</option>
                    </select>

                    <select
                        className="border rounded px-2 py-1 text-xs"
                        value={targetType}
                        onChange={(e) => setTargetType(e.target.value as any)}
                    >
                        <option value="">target: all</option>
                        <option value="all">all</option>
                        <option value="company">company</option>
                        <option value="shop">shop</option>
                    </select>

                    <button
                        className={btn}
                        onClick={() => {
                            setQ("");
                            setOnlyActive(false);
                            setOnlyMine(false);
                            setOnlyGlobal(false);
                            setLevel("");
                            setTargetType("");
                        }}
                        disabled={!!busyId}
                    >
                        クリア
                    </button>
                </div>
            </div>

            {/* table */}
            <div className="bg-white border rounded-md overflow-hidden flex-1 min-h-0 flex flex-col">
                <div className="overflow-auto flex-1 min-h-0">
                    <table className="w-full text-xs border-separate border-spacing-0">
                        <colgroup>
                            <col style={{ width: "5.5rem" }} />
                            <col style={{ width: "7rem" }} />
                            <col />
                            <col style={{ width: "8rem" }} />
                            <col style={{ width: "5rem" }} />
                            <col style={{ width: "7rem" }} />
                            <col style={{ width: "10rem" }} />
                        </colgroup>

                        <thead>
                            <tr>
                                <th className={th}>状態</th>
                                <th className={th}>重要度</th>
                                <th className={th}>タイトル</th>
                                <th className={th}>テナント</th>
                                <th className={th}>全体</th>
                                <th className={th}>対象</th>
                                <th className={th}>操作</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleRows.map((r) => {
                                const isPending = pendingDeleteId === r.id;
                                const editable = canEditRow(r);
                                const deletable = canDeleteRow(r);

                                const tenantLabel = String(r.tenantId ?? "").trim() || "-";
                                const isGlobal = r.isGlobal === true;
                                const isBusy = busyId === r.id;

                                return (
                                    <tr key={r.id} className="hover:bg-gray-50">
                                        <td className={`${td} text-center`}>
                                            <button
                                                onClick={() => toggleActive(r)}
                                                disabled={!editable || isBusy}
                                                className={[
                                                    "inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold",
                                                    activeBadge(r.isActive),
                                                    !editable ? "opacity-50 cursor-not-allowed" : "hover:opacity-90",
                                                ].join(" ")}
                                                title={!editable ? "この通知は閲覧のみです（編集不可）" : "ON/OFF 切替"}
                                            >
                                                {r.isActive ? "ON" : "OFF"}
                                            </button>
                                        </td>

                                        <td className={`${td} text-center`}>
                                            <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] ${levelBadge(r.level)}`}>
                                                {r.level}
                                            </span>
                                        </td>

                                        <td className={`${td} text-left`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="truncate" title={r.title}>
                                                    {r.title}
                                                </span>
                                                {!editable && (
                                                    <span className="text-[11px] px-2 py-0.5 rounded border text-gray-600 bg-gray-50 shrink-0">
                                                        閲覧のみ
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        <td className={`${td} text-center whitespace-nowrap`}>
                                            <span className="font-mono text-[11px]" title={tenantLabel}>
                                                {tenantLabel}
                                            </span>
                                        </td>

                                        <td className={`${td} text-center`}>
                                            {isGlobal ? (
                                                <span className="text-[11px] px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">
                                                    全体
                                                </span>
                                            ) : (
                                                <span className="text-[11px] px-2 py-0.5 rounded bg-gray-50 border text-gray-600">-</span>
                                            )}
                                        </td>

                                        <td className={`${td} text-center`}>
                                            <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] ${targetBadge(r.targetType)}`}>
                                                {r.targetType}
                                            </span>
                                        </td>

                                        <td className={`${td} text-center whitespace-nowrap`}>
                                            {!isPending ? (
                                                <div className="inline-flex items-center gap-2">
                                                    {editable ? (
                                                        <Link to={`/super/notifications/${r.id}`} className="text-blue-600 hover:underline text-xs">
                                                            編集
                                                        </Link>
                                                    ) : (
                                                        <span className="text-gray-300 text-xs">編集</span>
                                                    )}

                                                    {deletable ? (
                                                        <button
                                                            onClick={() => setPendingDeleteId(r.id)}
                                                            className="text-red-600 hover:underline text-xs disabled:opacity-50"
                                                            disabled={isBusy}
                                                        >
                                                            削除
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-300 text-xs">削除</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center gap-2">
                                                    <span className="text-[11px] text-gray-600">削除？</span>
                                                    <button
                                                        onClick={() => doDelete(r)}
                                                        className="text-[11px] px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                                                        disabled={!deletable || isBusy}
                                                        title={!deletable ? "削除できません" : undefined}
                                                    >
                                                        確定
                                                    </button>
                                                    <button
                                                        onClick={() => setPendingDeleteId(null)}
                                                        className="text-[11px] px-2 py-0.5 rounded border hover:bg-gray-50 disabled:opacity-50"
                                                        disabled={isBusy}
                                                    >
                                                        戻す
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}

                            {visibleRows.length === 0 && (
                                <tr>
                                    <td className="px-2 py-6 text-gray-500 text-xs" colSpan={7}>
                                        お知らせはまだありません（またはフィルタ条件で 0 件です）
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="px-2 py-1 text-[11px] text-gray-500 border-t">
                    ※ 自分のテナントのみ / 全体のみ / ONのみ などで絞り込めます（busy中は一部操作が無効化されます）
                </div>
            </div>
        </div>
    );
}
